"use client"

import { TrendingUp, Info } from "lucide-react"
import { Pie, PieChart } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"

interface ProductMarketShare {
  id: string;
  name: string;
  marketShare: number;
}

interface CategoryMarketSharePieChartsProps {
  preCampaignProducts: ProductMarketShare[];
  currentProducts: ProductMarketShare[];
  brandName?: string;
}

export function CategoryMarketSharePieCharts({
  preCampaignProducts,
  currentProducts,
  brandName = "Brand",
}: CategoryMarketSharePieChartsProps) {
  // Calculate total brand market share for each period
  const preCampaignTotal = preCampaignProducts.reduce(
    (sum, p) => sum + (parseFloat(p.marketShare.toString()) || 0),
    0
  );
  const currentTotal = currentProducts.reduce(
    (sum, p) => sum + (parseFloat(p.marketShare.toString()) || 0),
    0
  );

  // Calculate competitors' share (100% - brand total)
  const preCampaignCompetitors = Math.max(0, 100 - preCampaignTotal);
  const currentCompetitors = Math.max(0, 100 - currentTotal);

  // Get all unique product names across both periods
  const allProductNames = Array.from(
    new Set([
      ...preCampaignProducts.map((p) => p.name).filter(Boolean),
      ...currentProducts.map((p) => p.name).filter(Boolean),
    ])
  );

  // Define distinct colors for products (avoiding chart-2 which is for competitors)
  // Use chart-1, chart-3, chart-4, chart-5, and cycle through
  const productColors = [
    "var(--chart-1)",  // First product
    "var(--chart-3)",  // Second product
    "var(--chart-4)",  // Third product
    "var(--chart-5)",  // Fourth product
    "hsl(142, 76%, 36%)",  // Green for 5th product
    "hsl(280, 91%, 65%)",  // Purple for 6th product
    "hsl(24, 95%, 53%)",   // Orange for 7th product
    "hsl(199, 89%, 48%)",  // Cyan for 8th product
  ];

  // Prepare chart data: all products + competitors
  // Pre-Campaign: all products + competitors
  const preCampaignChartData = [
    ...preCampaignProducts.map((product, index) => {
      const productName = product.name || "Unknown Product";
      const colorIndex = allProductNames.indexOf(productName);
      return {
        name: productName,
        marketShare: parseFloat(product.marketShare.toString()) || 0,
        fill: productColors[colorIndex % productColors.length],
      };
    }),
    {
      name: "Competitors",
      marketShare: preCampaignCompetitors,
      fill: "var(--chart-2)", // Always use chart-2 for competitors
    },
  ];

  // Current: all products + competitors
  const currentChartData = [
    ...currentProducts.map((product, index) => {
      const productName = product.name || "Unknown Product";
      const colorIndex = allProductNames.indexOf(productName);
      return {
        name: productName,
        marketShare: parseFloat(product.marketShare.toString()) || 0,
        fill: productColors[colorIndex % productColors.length],
      };
    }),
    {
      name: "Competitors",
      marketShare: currentCompetitors,
      fill: "var(--chart-2)", // Always use chart-2 for competitors
    },
  ];

  // Create chart config
  const chartConfig: ChartConfig = {
    marketShare: {
      label: "Market Share",
    },
    Competitors: {
      label: "Competitors",
      color: "var(--chart-2)", // Distinct gray/neutral color for competitors
    },
  };

  // Add product colors - use same colors as in data
  allProductNames.forEach((productName, index) => {
    chartConfig[productName] = {
      label: productName,
      color: productColors[index % productColors.length],
    };
  });

  // Calculate percentage change
  const percentChange =
    preCampaignTotal > 0
      ? ((currentTotal - preCampaignTotal) / preCampaignTotal) * 100
      : 0;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Category Market Share</CardTitle>
        <CardDescription>
          Market share comparison: Pre-Campaign vs Current
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pre-Campaign Chart */}
          <Card className="flex flex-col">
            <CardHeader className="items-center pb-0">
              <CardTitle className="text-lg">Pre-Campaign</CardTitle>
              <CardDescription>
                Total Brand: {preCampaignTotal.toFixed(1)}%
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-0">
              <ChartContainer
                config={chartConfig}
                className="[&_.recharts-pie-label-text]:fill-foreground mx-auto aspect-square max-h-[300px] pb-0"
              >
                <PieChart>
                  <ChartTooltip
                    content={<ChartTooltipContent 
                      formatter={(value: any, name: any) => {
                        const numValue = typeof value === 'number' ? value : parseFloat(value);
                        return `${name}: ${numValue.toFixed(2)}%`;
                      }}
                    />}
                  />
                  <Pie
                    data={preCampaignChartData}
                    dataKey="marketShare"
                    label={({ marketShare }) => `${marketShare.toFixed(2)}%`}
                    labelLine={false}
                    nameKey="name"
                    outerRadius={90}
                  />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Current Chart */}
          <Card className="flex flex-col">
            <CardHeader className="items-center pb-0">
              <CardTitle className="text-lg">Current</CardTitle>
              <CardDescription>
                Total Brand: {currentTotal.toFixed(1)}%
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-0">
              <ChartContainer
                config={chartConfig}
                className="[&_.recharts-pie-label-text]:fill-foreground mx-auto aspect-square max-h-[300px] pb-0"
              >
                <PieChart>
                  <ChartTooltip
                    content={<ChartTooltipContent 
                      formatter={(value: any, name: any) => {
                        const numValue = typeof value === 'number' ? value : parseFloat(value);
                        return `${name}: ${numValue.toFixed(2)}%`;
                      }}
                    />}
                  />
                  <Pie
                    data={currentChartData}
                    dataKey="marketShare"
                    label={({ marketShare }) => `${marketShare.toFixed(2)}%`}
                    labelLine={false}
                    nameKey="name"
                    outerRadius={90}
                  />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-3 text-xl">
        <div className="flex items-center gap-2 leading-tight font-bold">
          {percentChange > 0 ? (
            <span className="text-green-600 text-2xl">
              +{percentChange.toFixed(1)}% increase from pre-campaign{" "}
              <TrendingUp className="h-7 w-7 inline" />
            </span>
          ) : percentChange < 0 ? (
            <span className="text-2xl">
              Trending down by {Math.abs(percentChange).toFixed(1)}% from
              pre-campaign
            </span>
          ) : (
            <span className="text-2xl">No change from pre-campaign</span>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center justify-center rounded-full hover:bg-muted p-1">
                <Info className="h-4 w-4 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Calculation</h4>
                <div className="text-sm space-y-1">
                  <p className="text-muted-foreground">
                    Percentage change formula:
                  </p>
                  <div className="font-mono text-xs bg-muted p-2 rounded">
                    ((Current - Pre-Campaign) / Pre-Campaign) × 100
                  </div>
                  <div className="pt-2 space-y-1">
                    <p>
                      <span className="font-medium">Pre-Campaign:</span> {preCampaignTotal.toFixed(2)}%
                    </p>
                    <p>
                      <span className="font-medium">Current:</span> {currentTotal.toFixed(2)}%
                    </p>
                    <p>
                      <span className="font-medium">Change:</span> {currentTotal.toFixed(2)}% - {preCampaignTotal.toFixed(2)}% = {(currentTotal - preCampaignTotal).toFixed(2)}%
                    </p>
                    <p>
                      <span className="font-medium">Percentage Change:</span> ({currentTotal.toFixed(2)}% - {preCampaignTotal.toFixed(2)}%) / {preCampaignTotal.toFixed(2)}% × 100 = {percentChange.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardFooter>
    </Card>
  );
}

